import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Phone, Instagram, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SITE, buildWaUrl } from "../data/site";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { BoltIcon } from "../components/Decorations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service: "",
  area: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.name.length < 2 || form.phone.length < 6 || form.message.length < 2) {
      toast.error("Mohon lengkapi nama, nomor WhatsApp, dan pesan.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        email: form.email || undefined,
        service: form.service || undefined,
        area: form.area || undefined,
      });
      toast.success(
        "Pesan terkirim! Kami akan menghubungi Anda via WhatsApp segera."
      );
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Gagal mengirim. Coba chat WhatsApp langsung ya.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Kontak ListrikBatam.com — Hubungi Teknisi Listrik Batam"
        description="Hubungi ListrikBatam.com via WhatsApp, email, atau formulir. Alamat: Tiban Koperasi E 12 B Batam. Respon cepat, teknisi siap ke lokasi."
        canonical="https://listrikbatam.com/kontak"
      />

      {/* Hero */}
      <section className="relative bg-[#0F4C75] text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-25" />
        <BoltIcon className="absolute top-10 right-10 w-16 h-24 opacity-40 float-slow" color="#FF6B00" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-24 pb-16">
          <Reveal>
            <div className="text-[#FFB341] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Kontak
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black leading-[0.98] tracking-tight max-w-3xl text-balance">
              Mari bicara soal <span className="text-[#FF6B00]">listrik Anda.</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/80 text-base md:text-lg">
              Isi formulir di bawah atau langsung chat WhatsApp — kami balas
              dalam 15 menit (jam operasional).
            </p>
          </Reveal>
        </div>
      </section>

      {/* Main grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Info */}
        <Reveal className="lg:col-span-5 space-y-4" stagger>
          {[
            {
              icon: Phone,
              title: "WhatsApp",
              value: "+62 811-7006-5656",
              href: buildWaUrl(),
              external: true,
              accent: true,
              testid: "contact-whatsapp",
            },
            {
              icon: Mail,
              title: "Email",
              value: SITE.email,
              href: `mailto:${SITE.email}`,
              testid: "contact-email",
            },
            {
              icon: Instagram,
              title: "Instagram",
              value: SITE.instagramHandle,
              href: SITE.instagram,
              external: true,
              testid: "contact-instagram",
            },
            {
              icon: MapPin,
              title: "Alamat Kantor",
              value: SITE.address,
              href: SITE.mapsLink,
              external: true,
              testid: "contact-address",
            },
          ].map(({ icon: Icon, title, value, href, external, accent, testid }) => (
            <a
              key={title}
              href={href}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              data-testid={testid}
              className={`group flex items-center gap-5 rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${
                accent
                  ? "bg-[#FF6B00] text-white border-[#FF6B00] hover:shadow-[0_14px_30px_rgba(255,107,0,0.35)]"
                  : "bg-white border-[#E5E7EB] text-[#1B262C] hover:border-[#3282B8]"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  accent ? "bg-white/20 text-white" : "bg-[#F8FAFC] text-[#0F4C75] group-hover:bg-[#FF6B00] group-hover:text-white transition-colors"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className={`text-xs uppercase tracking-wider font-semibold ${
                  accent ? "text-white/80" : "text-[#1B262C]/60"
                }`}>
                  {title}
                </div>
                <div className="font-display font-bold text-base md:text-lg mt-0.5 break-all">
                  {value}
                </div>
              </div>
            </a>
          ))}

          <div className="rounded-2xl overflow-hidden border border-[#E5E7EB] mt-2">
            <iframe
              title="Lokasi ListrikBatam.com"
              src={SITE.mapsEmbed}
              className="w-full h-64 md:h-72"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>

        {/* Form */}
        <Reveal className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="rounded-3xl bg-white border border-[#E5E7EB] p-6 md:p-10 shadow-[0_24px_60px_-30px_rgba(15,76,117,0.2)]"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0F4C75]">
              Kirim pesan
            </h2>
            <p className="text-sm text-[#1B262C]/70 mt-1 mb-6">
              Ceritakan kebutuhan listrik Anda. Semakin detail, semakin akurat
              estimasi kami.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Nama lengkap *"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Nama Anda"
                required
                testid="contact-field-name"
              />
              <Field
                label="Nomor WhatsApp *"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="0811-..."
                required
                testid="contact-field-phone"
              />
              <Field
                label="Email (opsional)"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="nama@email.com"
                testid="contact-field-email"
              />
              <Field
                label="Area lokasi"
                name="area"
                value={form.area}
                onChange={handleChange}
                placeholder="Contoh: Batu Aji, Tiban, Nagoya"
                testid="contact-field-area"
              />
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#1B262C]/70 mb-2">
                  Jenis layanan
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  data-testid="contact-field-service"
                  className="w-full rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 text-sm text-[#1B262C] focus:outline-none focus:ring-2 focus:ring-[#0F4C75]/30 focus:border-[#0F4C75] transition"
                >
                  <option value="">— Pilih layanan —</option>
                  <option>Instalasi & Perbaikan Listrik</option>
                  <option>Service AC</option>
                  <option>Instalasi CCTV</option>
                  <option>Service Mesin Cuci</option>
                  <option>Service Kulkas</option>
                  <option>Instalasi Water Heater</option>
                  <option>Panel & Industri</option>
                  <option>Perbaikan Modul PCB</option>
                  <option>Lainnya</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#1B262C]/70 mb-2">
                  Pesan / kondisi saat ini *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-field-message"
                  placeholder="Ceritakan masalah atau kebutuhan Anda..."
                  className="w-full rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 text-sm text-[#1B262C] focus:outline-none focus:ring-2 focus:ring-[#0F4C75]/30 focus:border-[#0F4C75] transition resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-7 inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#ff7a1f] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 text-sm shadow-[0_12px_28px_rgba(255,107,0,0.4)] hover:-translate-y-0.5 transition-all"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Mengirim...
                </>
              ) : (
                <>
                  Kirim pesan
                  <Send className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-[#1B262C]/55">
              Dengan mengirim pesan Anda menyetujui dihubungi tim ListrikBatam.com
              via WhatsApp / email untuk respon pesanan Anda.
            </p>
          </form>
        </Reveal>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#1B262C]/70 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3 text-sm text-[#1B262C] placeholder:text-[#1B262C]/40 focus:outline-none focus:ring-2 focus:ring-[#0F4C75]/30 focus:border-[#0F4C75] transition"
      />
    </div>
  );
}
